import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Typography, Breadcrumbs, Link, LinearProgress, Box, InputBase, Autocomplete, IconButton, useTheme, useMediaQuery } from '@mui/material'
import { ArrowDropDown } from '@mui/icons-material'

import { useOrgParams } from '~/common/use-org-params'
import { useOrgContext } from '../common/context/organization/org-context'
import { supabaseGetter } from '~/api/supabase-getter'
import { matchSorter } from 'match-sorter'
import { DESKTOP_BREAKPOINT } from '~/common/constants/layout-constants'

const usePlots = ({ farmId }) => {
  const [plots, setPlots] = useState([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    async function fetch() {
      if (!farmId) { setPlots([]); setLoading(false); return }
      setPlots([])
      setLoading(true)
      try {
        const { data } = await supabaseGetter.organizations.getPlots(farmId)
        setPlots(data || [])
      } finally {
        setLoading(false)
      }
    }
    fetch()
  }, [farmId])

  return { plots, loading }
}

const usePatches = ({ plotId }) => {
  const [patches, setPatches] = useState([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    async function fetch() {
      if (!plotId) { setPatches([]); setLoading(false); return }
      setPatches([])
      setLoading(true)
      try {
        const { data } = await supabaseGetter.organizations.getPatches(plotId)
        setPatches(data || [])
      } finally {
        setLoading(false)
      }
    }
    fetch()
  }, [plotId])

  return { patches, loading }
}

const filterOptions = (options, { inputValue }) => {
  if (!inputValue) return options
  return matchSorter(options, inputValue, { keys: ['name'] })
}

const BreadcrumbSelector = ({ value, options, onChange, isActive }) => {
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up(DESKTOP_BREAKPOINT))
  const [open, setOpen] = useState(false)

  return (
    <Autocomplete
      disablePortal
      disableClearable
      blurOnSelect
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={options}
      value={value}
      onChange={(_, newValue) => newValue && onChange(newValue)}
      filterOptions={filterOptions}
      getOptionLabel={(opt) => opt?.name ?? ''}
      isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
      slotProps={{
        popper: {
          placement: 'bottom-start',
          sx: { width: 'fit-content !important', maxWidth: '250px' },
        },
        paper: {
          sx: {
            '@keyframes bCrumbExpand': {
              from: { transform: 'scaleY(0.85)', opacity: 0 },
              to: { transform: 'scaleY(1)', opacity: 1 },
            },
            transformOrigin: 'top',
            animation: 'bCrumbExpand 120ms ease-out',
          },
        },
        listbox: {
          sx: { '& .MuiAutocomplete-option': { whiteSpace: 'nowrap' } },
        },
      }}
      renderInput={(params) => {
        const { endAdornment: _endAdornment, ...restInputProps } = params.InputProps
        return (
          <InputBase
            {...restInputProps}
            inputProps={{
              ...params.inputProps,
              placeholder: value ? undefined : 'Select plot',
              style: { width: value ? `${value.name.length}ch` : '5em' },
            }}
            endAdornment={
              <IconButton
                onClick={() => setOpen((prev) => !prev)}
                onMouseDown={(e) => e.preventDefault()}
                size="small"
                sx={{ p: 0, color: 'inherit' }}
              >
                <ArrowDropDown sx={{ fontSize: 20 }} />
              </IconButton>
            }
            sx={{
              fontSize: isDesktop ? 18 : 16,
              lineHeight: 0.9,
              p: 0,
              color: isActive ? 'text.primary' : 'text.secondary',
              '&:hover': { color: 'text.primary' },
              '& input': { p: 0, cursor: 'pointer' },
            }}
          />
        )
      }}
      sx={{ display: 'inline-flex', alignItems: 'center' }}
    />
  )
}

export const OrgBreadcrumbs = ({ baseRoute = 'dashboard' }) => {
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up(DESKTOP_BREAKPOINT))
  const { farmId, plotId, patchId } = useOrgParams()
  const navigate = useNavigate()
  const { activeOrgs = [] } = useOrgContext()
  const [farmOrg = null, plotOrg = null, patchOrg = null] = activeOrgs
  const farmName = farmOrg?.name ?? ''
  const { plots, loading: plotsLoading } = usePlots({ farmId })
  const { patches, loading: patchesLoading } = usePatches({ plotId })

  // orgsLoading: farmId is known from URL but the org data hasn't arrived from context yet
  const orgsLoading = !!farmId && !farmOrg
  const isLoading = orgsLoading || plotsLoading || patchesLoading

  const handlePlotChange = (plot) =>
    navigate(`/${baseRoute}?farmId=${farmId}&plotId=${plot.id}`)

  const handlePatchChange = (patch) =>
    navigate(`/${baseRoute}?farmId=${farmId}&plotId=${plotId}&patchId=${patch.id}`)

  return (
    <Box sx={{ p: '0.5rem 0 1rem 0', height: '1.5rem', display: 'flex', alignItems: 'center' }}>
      {isLoading ? (
        <LinearProgress sx={{ width: '30vw' }} />
      ) : (
        <Breadcrumbs
          sx={{
            '& .MuiTypography-root': {
              lineHeight: 0.9,
              display: 'flex',
              fontSize: isDesktop ? 18 : 16,
            },
            '& .MuiBreadcrumbs-separator': {
              lineHeight: 0.9,
              color: 'text.primary',
            },
          }}
        >
          <Link
            underline="hover"
            color={plotId ? 'inherit' : 'text.primary'}
            href={`/${baseRoute}?farmId=${farmId}`}
            onClick={(e) => {
              e.preventDefault()
              navigate(`/${baseRoute}?farmId=${farmId}`)
            }}
          >
            {farmName}
          </Link>
          {plots.length > 0 && (
            <BreadcrumbSelector
              value={plotOrg}
              options={plots}
              onChange={handlePlotChange}
              isActive={!!plotId && !patchId}
            />
          )}
          {!!plotId && patches.length > 0 && (
            <BreadcrumbSelector
              value={patchOrg}
              options={patches}
              onChange={handlePatchChange}
              isActive={!!patchId}
            />
          )}
        </Breadcrumbs>
      )}
    </Box>
  )
}
